// ===== modifier-service.ts ===================================================
import { Injectable } from '@angular/core';
import {
    ClaimLine,
    AdjustedLine,
    ModifierContext,
    ModifierResult,
    CoreModifier
} from './modifier-models';
import { Rule } from './rule-base';
import { RuleProviderRegistry } from './rule-provider-registry';
import { PhysiotherapyRuleProvider } from './rule-providers/physiotherapy-rule-provider';

@Injectable({ providedIn: 'root' })
export class ModifierService {
    constructor(private ruleProviderRegistry: RuleProviderRegistry) {}

    analyse(lines: ClaimLine[], ctx: ModifierContext = {}, disciplineCode?: string): ModifierResult {
        // Get the appropriate rule provider based on discipline code
        const provider = this.ruleProviderRegistry.getProvider(disciplineCode || 'medical');
        
        // Get rules from the provider
        const rules = provider.getRules();
        
        // Initialize work array
        let work: AdjustedLine[] = lines.map(l => ({
            ...l,
            adjustedUnits: l.initialUnits ?? l.units ?? 0,
            adjustedRand: l.initialRand ?? 0,
            trace: ['init']
        }));
        
        const warnings: string[] = [];
        
        // Special handling for physiotherapy discipline
        if (disciplineCode === '072' && provider instanceof PhysiotherapyRuleProvider) {
            // For physiotherapy, process lines with attached modifiers using the specialized method
            const physioProvider = provider as PhysiotherapyRuleProvider;
            work = physioProvider.processLinesWithModifiers(lines);
        } else {
            // Apply rules from the provider for other disciplines
            rules.forEach(rule => {
                work = rule.apply(work, { ...ctx, lines: work }, warnings);
            });
        }
        
        return { lines: work, warnings };
    }

    /**
     * Validate if a modifier is applicable for the given discipline
     */
    validateModifier(modifierCode: string, line: ClaimLine, disciplineCode?: string): boolean {
        const provider = this.ruleProviderRegistry.getProvider(disciplineCode || 'medical');
        return provider.validateModifier(modifierCode, line);
    }

    /**
     * Get discipline-specific context
     */
    getContext(lines: ClaimLine[], disciplineCode?: string): ModifierContext {
        const provider = this.ruleProviderRegistry.getProvider(disciplineCode || 'medical');
        return provider.getContext(lines);
    }

    /**
     * Get supported discipline codes
     */
    getSupportedDisciplines(): string[] {
        return this.ruleProviderRegistry.getSupportedDisciplines();
    }
}